import { useCallback, useEffect, useState } from "react";
import { Button, Card, Empty, Segmented, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { listRuns } from "../../apiservice/workflow";
import { recordViewPath } from "./recordLink";

const { Text, Paragraph } = Typography;

function shortId(v) {
  if (!v) return "";
  const s = String(v);
  return s.length > 8 ? `${s.slice(0, 8)}...` : s;
}

function actionLabel(actionType) {
  if (!actionType) return "manual step";
  const map = {
    "Assign to User": "approval",
    "Assign to Role": "role approval",
    "Send Notification": "notification",
    "Send Email": "email",
    "Update Field": "field update",
    "Assign Task": "task completion",
  };
  return map[actionType] || actionType.toLowerCase();
}

function formatStuckMessage(row) {
  if (row.status !== "Running") return "—";
  if (!row.waiting_node_id) return "Running (waiting step not identified)";

  const subject = row.assigned_to_user_name
    ? `Waiting for ${row.assigned_to_user_name}`
    : row.assigned_to_user
      ? `Waiting for user ${shortId(row.assigned_to_user)}`
      : row.assigned_to_role
        ? `Waiting for role ${shortId(row.assigned_to_role)}`
        : "Waiting for next approval";

  const step = `at step ${row.waiting_node_id}`;
  const kind = actionLabel(row.waiting_action_type);
  return `${subject} (${kind}) ${step}`;
}

function RecordLink({ module, recordType, recordId }) {
  const path = recordViewPath(module || recordType, recordId);
  const label = <Text code>{String(recordId).slice(0, 8)}…</Text>;
  return (
    <Tooltip title={path ? `Open ${module || recordType} record` : recordId}>
      {path ? <Link to={path} onClick={(e) => e.stopPropagation()}>{label}</Link> : label}
    </Tooltip>
  );
}

const STATUS_COLORS = { Running: "orange", Completed: "green", Rejected: "red" };

/**
 * Dashboard of workflow instances — one row per (workflow, record). Shows the
 * overall state, who started it (the record creator) and who it is currently
 * waiting on. Filterable by status.
 */
export default function RunningInstances() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("All");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listRuns({ status: status.toLowerCase() });
      setRows(r?.data?.data || r?.data || []);
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to load workflows");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { refresh(); }, [refresh]);

  const who = (id, name) => (id ? <Tooltip title={id}><Text>{name || `user ${shortId(id)}`}</Text></Tooltip> : <Text type="secondary">—</Text>);

  return (
    <Card
      title="Workflow runs"
      extra={
        <Space>
          <Segmented
            value={status}
            onChange={setStatus}
            options={["Running", "Completed", "Rejected", "All"]}
          />
          <Button icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>
        </Space>
      }
      loading={loading}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Info: <b>Stuck at</b> shows the current waiting step. Pending assignments are actionable in <Text code>/workflow-tasks</Text> and in the right sidebar.
      </Paragraph>
      <Table
        rowKey={(r) => `${r.workflow_id}-${r.record_id}`}
        dataSource={rows}
        locale={{ emptyText: <Empty description={`No ${status.toLowerCase()} workflows`} /> }}
        pagination={{ pageSize: 10 }}
        columns={[
          {
            title: "Workflow",
            dataIndex: "workflow_name",
            render: (v) => v ? v : <Text type="secondary">Unknown workflow</Text>,
          },
          { title: "Module", dataIndex: "module", render: (v, r) => <Text code>{v || r.record_type}</Text> },
          { title: "Record", dataIndex: "record_id", render: (v, r) => <RecordLink module={r.module} recordType={r.record_type} recordId={v} /> },
          {
            title: "Status",
            dataIndex: "status",
            render: (s, r) => (
              <Tag color={STATUS_COLORS[s] || "default"}>
                {s}{s === "Running" && r.pending_count ? ` · ${r.pending_count} pending` : ""}
              </Tag>
            ),
          },
          {
            title: "Stuck at",
            render: (_, r) => {
              if (r.status !== "Running") return <Text type="secondary">—</Text>;
              const msg = formatStuckMessage(r);
              const overdue = !!(r.due_at && new Date(r.due_at) < new Date());
              return (
                <Space direction="vertical" size={0}>
                  <Text>{msg}</Text>
                  {overdue ? <Text type="danger" style={{ fontSize: 12 }}>Overdue</Text> : null}
                </Space>
              );
            },
          },
          { title: "Started by", render: (_, r) => who(r.started_by, r.started_by_name) },
          {
            title: "Assigned to",
            render: (_, r) => (
              r.assigned_to_user
                ? who(r.assigned_to_user, r.assigned_to_user_name)
                : (r.assigned_to_role ? <Tag color="purple">role {shortId(r.assigned_to_role)}</Tag> : <Text type="secondary">—</Text>)
            ),
          },
          {
            title: "Due",
            dataIndex: "due_at",
            render: (v) => (v ? <Tag color={new Date(v) < new Date() ? "red" : "default"}>{new Date(v).toLocaleString()}</Tag> : <Text type="secondary">—</Text>),
          },
          { title: "Started", dataIndex: "started_at", render: (v) => (v ? new Date(v).toLocaleString() : "—") },
        ]}
      />
    </Card>
  );
}
